import { MAX_STOCK_QUANTITY, normalizeStockQuantity } from './stock-summary';

export type StockTransferStatus = 'draft' | 'dispatched' | 'received';

export interface StockTransferLineLike {
  id?: string;
  articleId: string;
  requestedQuantity: number;
  receivedQuantity?: number | null;
}

export interface StockTransferLike {
  status: StockTransferStatus;
  originWarehouseId: string;
  destinationWarehouseId: string;
  lines?: readonly StockTransferLineLike[];
}

export interface StockLevelLike {
  articleId: string;
  warehouseId: string;
  quantity: number;
}

export interface ReceiptQuantityInput {
  lineId: string;
  receivedQuantity: number;
}

export interface StockTransferLedgerEffect {
  lineId: string;
  articleId: string;
  warehouseId: string;
  movementType: 'in' | 'out';
  quantity: number;
}

export class StockTransferValidationError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'StockTransferValidationError';
  }
}

function positiveQuantity(value: number, code: string): number {
  const normalized = normalizeStockQuantity(Number(value));
  if (
    !Number.isFinite(normalized) ||
    normalized <= 0 ||
    normalized > MAX_STOCK_QUANTITY
  ) {
    throw new StockTransferValidationError(code);
  }
  return normalized;
}

export function validateStockTransferDraft(input: {
  originWarehouseId: string;
  destinationWarehouseId: string;
  lines: readonly { articleId: string; quantity: number }[];
}): { articleId: string; quantity: number }[] {
  if (!input.originWarehouseId || !input.destinationWarehouseId) {
    throw new StockTransferValidationError('stockTransfer.warehousesRequired');
  }
  if (input.originWarehouseId === input.destinationWarehouseId) {
    throw new StockTransferValidationError('stockTransfer.sameWarehouse');
  }
  if (input.lines.length === 0) {
    throw new StockTransferValidationError('stockTransfer.linesRequired');
  }

  const quantities = new Map<string, number>();
  for (const line of input.lines) {
    if (!line.articleId) {
      throw new StockTransferValidationError('stockTransfer.articleRequired');
    }
    const quantity = positiveQuantity(
      line.quantity,
      'stockTransfer.quantityPositive',
    );
    const combined = normalizeStockQuantity(
      (quantities.get(line.articleId) ?? 0) + quantity,
    );
    if (combined > MAX_STOCK_QUANTITY) {
      throw new StockTransferValidationError('stockTransfer.quantityPositive');
    }
    quantities.set(line.articleId, combined);
  }

  return Array.from(quantities, ([articleId, quantity]) => ({
    articleId,
    quantity,
  }));
}

export function validateStockTransferDispatch(
  transfer: StockTransferLike,
  stock: readonly StockLevelLike[],
): void {
  if (transfer.status !== 'draft') {
    throw new StockTransferValidationError('stockTransfer.notDraft');
  }
  if (!transfer.lines?.length) {
    throw new StockTransferValidationError('stockTransfer.linesRequired');
  }

  const available = new Map<string, number>();
  for (const level of stock) {
    if (level.warehouseId !== transfer.originWarehouseId) continue;
    available.set(
      level.articleId,
      normalizeStockQuantity(
        (available.get(level.articleId) ?? 0) + Number(level.quantity || 0),
      ),
    );
  }

  for (const line of transfer.lines) {
    const requested = positiveQuantity(
      line.requestedQuantity,
      'stockTransfer.quantityPositive',
    );
    if ((available.get(line.articleId) ?? 0) < requested) {
      throw new StockTransferValidationError('stockTransfer.insufficientStock');
    }
  }
}

export function validateStockTransferReceipt(
  transfer: StockTransferLike,
  received: readonly ReceiptQuantityInput[],
): Map<string, number> {
  if (transfer.status !== 'dispatched') {
    throw new StockTransferValidationError('stockTransfer.notDispatched');
  }
  if (!transfer.lines?.length) {
    throw new StockTransferValidationError('stockTransfer.linesRequired');
  }

  const byLineId = new Map<string, number>();
  for (const input of received) {
    const quantity = normalizeStockQuantity(Number(input.receivedQuantity));
    if (
      !input.lineId ||
      !Number.isFinite(quantity) ||
      quantity < 0 ||
      quantity > MAX_STOCK_QUANTITY
    ) {
      throw new StockTransferValidationError(
        'stockTransfer.receivedQuantityInvalid',
      );
    }
    if (byLineId.has(input.lineId)) {
      throw new StockTransferValidationError(
        'stockTransfer.duplicateReceiptLine',
      );
    }
    byLineId.set(input.lineId, quantity);
  }

  for (const line of transfer.lines) {
    if (!line.id || !byLineId.has(line.id)) {
      throw new StockTransferValidationError(
        'stockTransfer.receiptLinesIncomplete',
      );
    }
    const quantity = byLineId.get(line.id) as number;
    if (quantity > normalizeStockQuantity(line.requestedQuantity)) {
      throw new StockTransferValidationError(
        'stockTransfer.receivedQuantityTooHigh',
      );
    }
  }
  if (byLineId.size !== transfer.lines.length) {
    throw new StockTransferValidationError(
      'stockTransfer.receiptLinesIncomplete',
    );
  }

  return byLineId;
}

export function buildStockTransferDispatchEffects(
  transfer: StockTransferLike,
): StockTransferLedgerEffect[] {
  return (transfer.lines ?? []).map((line) => {
    if (!line.id)
      throw new StockTransferValidationError('stockTransfer.linesRequired');
    return {
      lineId: line.id,
      articleId: line.articleId,
      warehouseId: transfer.originWarehouseId,
      movementType: 'out',
      quantity: normalizeStockQuantity(line.requestedQuantity),
    };
  });
}

export function buildStockTransferReceiptEffects(
  transfer: StockTransferLike,
  receivedByLineId: ReadonlyMap<string, number>,
): StockTransferLedgerEffect[] {
  return (transfer.lines ?? []).flatMap((line) => {
    if (!line.id)
      throw new StockTransferValidationError('stockTransfer.linesRequired');
    const quantity = normalizeStockQuantity(receivedByLineId.get(line.id) ?? 0);
    return quantity > 0
      ? [
          {
            lineId: line.id,
            articleId: line.articleId,
            warehouseId: transfer.destinationWarehouseId,
            movementType: 'in' as const,
            quantity,
          },
        ]
      : [];
  });
}
